import { useEffect, useCallback } from "react";
import { useRouter } from "next/router";
import { useMutation, useQueryClient } from "react-query";
import {
  usePlayback,
  usePlayer,
  usePlaybackTimeInterval,
  useIsAutoPlayed,
} from "@gonoodle/gn-universe-ui";

import { QUERY_KEYS, CONTENT_TYPES } from "../constants";
import { useUser } from "../contexts/user";
import { useLogEvent, getReferrerFromRouter } from "../contexts/Analytics";
import { getAnalyticsParams } from "../utils/analytics";
import api from "../api";

const client = api();

export default function useVideoSession(title) {
  const queryClient = useQueryClient();
  const router = useRouter();
  const player = usePlayer();
  const [playbackState] = usePlayback();
  const { user } = useUser();
  const isAutoPlayed = useIsAutoPlayed();
  const analyticsParams = getAnalyticsParams(router.query);
  const { identity } = router.query;
  const [videoId] = identity;

  const { mutate: createSession, data: session } = useMutation(
    () =>
      client.createActivitySession({
        videoVersionId: videoId,
        autoplayed: isAutoPlayed,
        ...(getReferrerFromRouter(router)
          ? getReferrerFromRouter(router)
          : analyticsParams),
      }),
    { retry: 6 },
  );
  const { mutate: persistSession } = useMutation(
    ({ sessionId, seconds }) =>
      client.updateActivitySession(sessionId, { seconds }),
    {
      onSuccess: ({ showAd }) => {
        if (user.showAd !== showAd) {
          queryClient.invalidateQueries(QUERY_KEYS.USER);
        }
      },
    },
  );

  useLogEvent({
    event: "Video Played",
    properties: {
      title,
      id: videoId,
      brainBreakSessionId: session?.activitySessionId,
      contentType: CONTENT_TYPES.VIDEO,
      autoPlayed: isAutoPlayed,
    },
    options: {
      enabled: !!session,
    },
  });

  const persistTime = useCallback(
    (interval) => {
      if (session) {
        persistSession({
          sessionId: session.activitySessionId,
          seconds: interval,
        });
      }
    },
    [persistSession, session],
  );

  useEffect(() => {
    if (player.isReady) {
      player.on("firstFrame", () => {
        createSession();
      });
    }
    return () => {
      if (player.isReady) {
        player.off("firstFrame");
      }
    };
  }, [createSession, player]);

  const { flush } = usePlaybackTimeInterval(persistTime, 10);

  useEffect(() => {
    if (playbackState.completed || playbackState.paused) {
      flush();
    }
  }, [flush, playbackState.completed, playbackState.paused]);

  useEffect(
    () => () => {
      flush();
    },
    [flush],
  );
}
